import "./sentry";
import "./index.css";

import { MerchantClient } from "@hobbyshopify/backend-client";
import { loadConfig } from "@hobbyshopify/vite-cfg";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { App } from "./app";
import { createAppDeps } from "./app-deps";
import { createFirebaseAuth, FirebaseSession } from "./platform/firebase-session";
import { SentryErrorBoundary } from "./sentry";

// This app is no longer a PWA and registers no service worker, so any worker
// controlling this origin is a leftover that would serve `npm run dev` the
// precached production shell. Drop it, and drop its caches with it.
if ("serviceWorker" in navigator) {
  void navigator.serviceWorker.getRegistrations().then(async (registrations) => {
    if (registrations.length === 0) return;
    await Promise.all(registrations.map((registration) => registration.unregister()));
    const names = await caches.keys();
    await Promise.all(names.map((name) => caches.delete(name)));
    console.warn("Removed a leftover service worker and its caches. Reload once to get fresh assets.");
  });
}

const root = document.getElementById("root");
if (!root) throw new Error("Root element not found");

/**
 * One stateless `MerchantClient` for the whole app: it holds configuration only,
 * and who is calling travels as the `ApiContext` every method takes first.
 * `baseUrl: "/api"` stays same-origin: the gateway routes it to the backend on
 * this host, and the Vite dev proxy stands in for it locally.
 */
const deps = createAppDeps({
  client: new MerchantClient({ baseUrl: "/api" }),
  session: new FirebaseSession(createFirebaseAuth()),
  stripePublishableKey: loadConfig("VITE_STRIPE_PUBLISHABLE_KEY") ?? null,
  publicSiteUrl: loadConfig("VITE_HOBBYSHOP_URL") ?? null,
});

createRoot(root).render(
  <StrictMode>
    <SentryErrorBoundary>
      <BrowserRouter>
        <App deps={deps} />
      </BrowserRouter>
    </SentryErrorBoundary>
  </StrictMode>,
);
